import { BankProvider, ExternalCardProvider } from '@getstep/proto/common'
import {
    CardIssuerProvider,
    DefaultAccessTokenProvider,
    OfferStore,
} from '@getstep/sdk'
import { Api } from '@getstep/sdk/dist/http/Api'
import { PublicApi } from '@getstep/sdk/dist/http/PublicApi'
import { AccountListStore } from '@getstep/sdk/dist/store/AccountListStore'
import { ExternalAccountsStore } from '@getstep/sdk/dist/store/ExternalAccountsStore'
import { FamilyStore } from '@getstep/sdk/dist/store/FamilyStore'
import { FeatureStore } from '@getstep/sdk/dist/store/features/FeatureStore'
import { TierStore } from '@getstep/sdk/dist/store/TierStore'
import { TransferStore } from '@getstep/sdk/dist/store/transfer/TransferStore'
import { UnderwritingApplicationStore } from '@getstep/sdk/dist/store/UnderwritingApplicationStore'
import { UserStore } from '@getstep/sdk/dist/store/UserStore'
import { mustExist } from '@getstep/sdk/dist/util/Assert'
import { DeviceStore } from '@lib/store/DeviceStore'
import { tokenStore } from '@lib/store/JwtTokenStore'
import type { LinkingStore } from '@lib/store/linking/LinkingStore'
import type { LocalFeatureOverridesStorage } from '@lib/store/LocalFeatureOverrideStore'
import { BorrowingStore } from '@lib/store/onboarding/BorrowingStore'
import type { SdkClientStore } from '@lib/store/SdkClientStore'
import type { SessionStore } from '@lib/store/session'
import { EnrollmentFlowManager } from '@lib/utils/enrollment-routing'

import { KYCStore } from './KYCStore'
import { SponsorRequestStore } from './SponsorRequestStore'

const stripeVersion = '2019-09-09'

/**
 * These stores require a logged in user to function.
 */
export class AuthorizedStore {
    // Enrollment flow manager created after store initialization
    enrollmentFlowManager!: EnrollmentFlowManager

    constructor(
        readonly latestStripeKey: string,
        readonly plaidLinkingToken: string,
        readonly sessionStore: SessionStore,
        readonly familyStore: FamilyStore,
        readonly userStore: UserStore,
        readonly sponsorRequestStore: SponsorRequestStore,
        readonly kycStore: KYCStore,
        readonly transferStore: TransferStore,
        readonly externalAccountsStore: ExternalAccountsStore,
        readonly featureStore: FeatureStore,
        readonly tierStore: TierStore,
        readonly accountListStore: AccountListStore,
        readonly cardIssuerProvider: CardIssuerProvider,
        readonly borrowingStore: BorrowingStore,
        readonly offerStore: OfferStore,
    ) {}

    static async create(
        sessionStore: SessionStore,
        linkingStore: LinkingStore,
        sdk: SdkClientStore,
        featureOverideStores: LocalFeatureOverridesStorage,
    ) {
        const refreshToken = mustExist(
            tokenStore.refreshToken,
            'refreshToken is required to create AuthorizedStore',
        )

        const apiClient = await sdk.createHttpClient()
        const stripeHttpClient = await sdk.createStripeHttpClient()
        const publicApi = new PublicApi(apiClient)

        const tokenProvider = new DefaultAccessTokenProvider(publicApi, {
            refreshToken,
        })

        const api = new Api(
            apiClient,
            tokenStore,
            tokenStore,
            tokenProvider,
            undefined,
            undefined,
            DeviceStore.fingerprint,
        )

        const familyStore = new FamilyStore(apiClient, api)
        const underwritingApplicationStore = new UnderwritingApplicationStore(
            api,
        )
        const userStore = new UserStore(api)
        const sponsorRequestStore = new SponsorRequestStore(
            familyStore,
            linkingStore,
            userStore,
        )
        const transferStore = new TransferStore(api)

        const externalAccountsStore = new ExternalAccountsStore(
            api,
            stripeHttpClient,
            undefined,
            true,
        )

        const featureStore = new FeatureStore(
            api,
            featureOverideStores,
            sessionStore.getClientId(),
        )

        const [latestStripeKey, plaidLinkingKey] = await Promise.all([
            api.getCardProviderKey(ExternalCardProvider.STRIPE),
            api.getBankLinkingToken(BankProvider.PLAID),
        ])
        const tierStore = new TierStore(api)
        const borrowStore = new BorrowingStore(underwritingApplicationStore)
        const cardIssuerProvider = new CardIssuerProvider(
            api,
            stripeHttpClient,
            process.env.NEXT_PUBLIC_STRIPE_ISSUING_KEY!,
            stripeVersion,
        )

        // load userStore and featureStore in all cases because its likely to be used in all authorized screens
        await Promise.all([
            userStore.load(),
            featureStore.load(),
            externalAccountsStore.load(),
            tierStore.load(),
        ])
        // Only load borrowing store if user is KYCed.
        if (userStore.isKYCed) {
            await borrowStore.load()
        }
        const accountListStore = new AccountListStore(api, userStore.user.id)
        await accountListStore.load()

        const offerStore = new OfferStore(api)

        // Create authorized store
        const authorizedStore = new AuthorizedStore(
            latestStripeKey.key,
            plaidLinkingKey.token,
            sessionStore,
            familyStore,
            userStore,
            sponsorRequestStore,
            new KYCStore(sessionStore, sponsorRequestStore),
            transferStore,
            externalAccountsStore,
            featureStore,
            tierStore,
            accountListStore,
            cardIssuerProvider,
            borrowStore,
            offerStore,
        )

        // Create enrollment flow manager (checks feature flag once per session)
        authorizedStore.enrollmentFlowManager = new EnrollmentFlowManager(
            authorizedStore,
        )

        return authorizedStore
    }
}
